import React, { useState, useEffect, useMemo } from 'react';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

export default function BreakpointMasterPage() {
  const [breakpoints, setBreakpoints] = useState([]);
  const [standards, setStandards] = useState([]);
  const [organisms, setOrganisms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [antimicrobials, setAntimicrobials] = useState([]);
  const [astMethods, setAstMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [selectedOrganism, setSelectedOrganism] = useState('');
  const [selectedDrug, setSelectedDrug] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');

  // Interactive Live Calculation Sandbox
  const [calcOrgId, setCalcOrgId] = useState('');
  const [calcDrugId, setCalcDrugId] = useState('');
  const [calcMethodId, setCalcMethodId] = useState(1);
  const [calcType, setCalcType] = useState('ZONE');
  const [calcValue, setCalcValue] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);

  // Configure Breakpoint Rule Modal Form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBreakpoint, setEditingBreakpoint] = useState(null);
  const [targetLevel, setTargetLevel] = useState('GROUP'); // 'GROUP' or 'SPECIFIC'
  const [formData, setFormData] = useState({
    versionID: 1,
    organismGroupID: '',
    organismID: '',
    antimicrobialID: '',
    astMethodID: 1,
    siteOfInfection: 'Systemic',
    breakPointType: 'ZONE',
    sOperator: '>=',
    susceptibleThreshold: 20,
    intermediateMin: 17,
    intermediateMax: 19,
    rOperator: '<',
    resistantThreshold: 17,
    ruleExplanation: '',
    referenceNotes: '',
    status: 'Draft',
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  const loadData = async () => {
    try {
      setLoading(true);
      const [bpRes, stdRes, orgRes, grpRes, drugRes, mRes] = await Promise.all([
        microbiologyService.getBreakpoints({
          versionId: selectedVersion || undefined,
          organismId: selectedOrganism || undefined,
          antimicrobialId: selectedDrug || undefined
        }),
        microbiologyService.getStandards(),
        microbiologyService.getOrganisms(),
        microbiologyService.getOrganismGroups(),
        microbiologyService.getAntimicrobials(),
        microbiologyService.getASTMethods()
      ]);

      setBreakpoints(bpRes?.data || []);
      setStandards(stdRes?.data || []);
      setOrganisms(orgRes?.data || []);
      setGroups(grpRes?.data || []);
      setAntimicrobials(drugRes?.data || []);
      setAstMethods(mRes?.data || []);

      if (orgRes?.data?.length > 0 && !calcOrgId) {
        setCalcOrgId(orgRes.data[0].organismID);
      }
      if (drugRes?.data?.length > 0 && !calcDrugId) {
        setCalcDrugId(drugRes.data[0].antimicrobialID);
      }
    } catch (err) {
      console.error('Error loading breakpoint data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedVersion, selectedOrganism, selectedDrug]);

  // Sync measurement type when AST method changes in sandbox
  const handleCalcMethodChange = (methodId) => {
    const mId = parseInt(methodId, 10);
    setCalcMethodId(mId);
    const method = astMethods.find((m) => m.astMethodID === mId);
    if (method) {
      setCalcType(method.measurementType || 'ZONE');
    }
  };

  // Run S/I/R Calculation Engine
  const handleRunCalculation = async () => {
    setCalcError(null);
    if (!calcOrgId || !calcDrugId || calcValue === '') {
      setCalcError('Please select organism, drug, and enter a numeric measured value.');
      return;
    }

    const numVal = parseFloat(calcValue);
    if (isNaN(numVal) || numVal < 0) {
      setCalcError('Please enter a valid non-negative numeric measurement.');
      return;
    }

    try {
      setCalcLoading(true);
      const res = await microbiologyService.calculateAST({
        organismID: parseInt(calcOrgId, 10),
        antimicrobialID: parseInt(calcDrugId, 10),
        astMethodID: parseInt(calcMethodId, 10),
        measuredValue: numVal
      });
      setCalcResult(res?.data || null);
    } catch (err) {
      setCalcError(err.response?.data?.message || 'Error executing S/I/R calculation engine.');
    } finally {
      setCalcLoading(false);
    }
  };

  // Open Add Breakpoint Modal
  const handleOpenAdd = () => {
    setEditingBreakpoint(null);
    setTargetLevel('GROUP');
    setValidationErrors({});
    const initialGrp = groups[0]?.organismGroupID || 1;
    const initialDrug = antimicrobials[0]?.antimicrobialID || 1;
    const initialMethod = astMethods[0]?.astMethodID || 1;

    setFormData({
      versionID: 1,
      organismGroupID: initialGrp,
      organismID: '',
      antimicrobialID: initialDrug,
      astMethodID: initialMethod,
      siteOfInfection: 'Systemic',
      breakPointType: 'ZONE',
      sOperator: '>=',
      susceptibleThreshold: 20,
      intermediateMin: 17,
      intermediateMax: 19,
      rOperator: '<',
      resistantThreshold: 17,
      ruleExplanation: '',
      referenceNotes: '',
      status: 'Draft',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: ''
    });
    setIsModalOpen(true);
  };

  // Open Edit Breakpoint Modal
  const handleOpenEdit = (bp) => {
    setEditingBreakpoint(bp);
    setValidationErrors({});
    const isSpecific = !!bp.organismID;
    setTargetLevel(isSpecific ? 'SPECIFIC' : 'GROUP');

    setFormData({
      breakpointID: bp.breakpointID,
      versionID: bp.versionID || 1,
      organismGroupID: bp.organismGroupID || '',
      organismID: bp.organismID || '',
      antimicrobialID: bp.antimicrobialID,
      astMethodID: bp.astMethodID,
      siteOfInfection: bp.siteOfInfection || 'Systemic',
      breakPointType: bp.breakPointType || 'ZONE',
      sOperator: bp.breakPointType === 'ZONE' ? '>=' : '<=',
      susceptibleThreshold: bp.susceptibleThreshold !== null ? bp.susceptibleThreshold : '',
      intermediateMin: bp.intermediateMin !== null ? bp.intermediateMin : '',
      intermediateMax: bp.intermediateMax !== null ? bp.intermediateMax : '',
      rOperator: bp.breakPointType === 'ZONE' ? '<' : '>',
      resistantThreshold: bp.resistantThreshold !== null ? bp.resistantThreshold : '',
      ruleExplanation: bp.ruleExplanation || '',
      referenceNotes: '',
      status: bp.isActive ? 'Active' : 'Draft',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: ''
    });
    setIsModalOpen(true);
  };

  // Duplicate Rule as New Version
  const handleDuplicate = (bp) => {
    setEditingBreakpoint(null);
    setValidationErrors({});
    const isSpecific = !!bp.organismID;
    setTargetLevel(isSpecific ? 'SPECIFIC' : 'GROUP');

    setFormData({
      versionID: bp.versionID || 1,
      organismGroupID: bp.organismGroupID || '',
      organismID: bp.organismID || '',
      antimicrobialID: bp.antimicrobialID,
      astMethodID: bp.astMethodID,
      siteOfInfection: bp.siteOfInfection || 'Systemic',
      breakPointType: bp.breakPointType || 'ZONE',
      sOperator: bp.breakPointType === 'ZONE' ? '>=' : '<=',
      susceptibleThreshold: bp.susceptibleThreshold,
      intermediateMin: bp.intermediateMin,
      intermediateMax: bp.intermediateMax,
      rOperator: bp.breakPointType === 'ZONE' ? '<' : '>',
      resistantThreshold: bp.resistantThreshold,
      ruleExplanation: bp.ruleExplanation || '',
      referenceNotes: `Revision based on Rule #${bp.breakpointID}`,
      status: 'Draft',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: ''
    });
    setIsModalOpen(true);
  };

  // Generate real-time rule trace preview
  const ruleTracePreview = useMemo(() => {
    const std = standards.find((s) => s.versions?.some((v) => v.versionID === formData.versionID));
    const stdName = std ? std.standardCode : 'CLSI/EUCAST';
    
    let targetName = 'All Organisms';
    if (targetLevel === 'SPECIFIC' && formData.organismID) {
      const org = organisms.find((o) => o.organismID === parseInt(formData.organismID, 10));
      if (org) targetName = org.organismName;
    } else if (formData.organismGroupID) {
      const grp = groups.find((g) => g.organismGroupID === parseInt(formData.organismGroupID, 10));
      if (grp) targetName = grp.groupName;
    }

    const drug = antimicrobials.find((d) => d.antimicrobialID === parseInt(formData.antimicrobialID, 10));
    const drugName = drug ? `${drug.drugName} (${drug.drugCode})` : 'Antimicrobial';
    
    const unit = formData.breakPointType === 'ZONE' ? 'mm' : 'µg/mL';
    const sOp = formData.breakPointType === 'ZONE' ? '≥' : '≤';
    const rOp = formData.breakPointType === 'ZONE' ? '<' : '>';

    const sStr = formData.susceptibleThreshold !== '' ? `S ${sOp} ${formData.susceptibleThreshold} ${unit}` : '';
    const iStr = formData.intermediateMin !== '' && formData.intermediateMax !== ''
      ? `I ${formData.intermediateMin}–${formData.intermediateMax} ${unit}`
      : '';
    const rStr = formData.resistantThreshold !== '' ? `R ${rOp} ${formData.resistantThreshold} ${unit}` : '';

    const thresholds = [sStr, iStr, rStr].filter(Boolean).join(', ');
    return `${stdName} — ${drugName} for ${targetName} [${formData.breakPointType}] (${thresholds || 'No thresholds specified'})`;
  }, [formData, targetLevel, standards, organisms, groups, antimicrobials]);

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!formData.versionID) {
      errors.versionID = 'Standard version is required';
    }

    if (targetLevel === 'SPECIFIC' && !formData.organismID) {
      errors.organismID = 'Specific organism must be selected';
    } else if (targetLevel === 'GROUP' && !formData.organismGroupID) {
      errors.organismGroupID = 'Taxonomic group must be selected';
    }

    if (!formData.antimicrobialID) {
      errors.antimicrobialID = 'Antimicrobial drug is required';
    }

    if (!formData.astMethodID) {
      errors.astMethodID = 'AST Method is required';
    }

    if (formData.susceptibleThreshold === '' || isNaN(formData.susceptibleThreshold)) {
      errors.susceptibleThreshold = 'Valid susceptible threshold is required';
    }

    if (formData.resistantThreshold === '' || isNaN(formData.resistantThreshold)) {
      errors.resistantThreshold = 'Valid resistant threshold is required';
    }

    // Mathematical boundary check
    const sVal = parseFloat(formData.susceptibleThreshold);
    const rVal = parseFloat(formData.resistantThreshold);
    const iMin = formData.intermediateMin !== '' ? parseFloat(formData.intermediateMin) : null;
    const iMax = formData.intermediateMax !== '' ? parseFloat(formData.intermediateMax) : null;

    if (!isNaN(sVal) && !isNaN(rVal)) {
      if (formData.breakPointType === 'ZONE' && sVal <= rVal) {
        errors.boundary = 'For Zone diameter, Susceptible threshold (≥) must be strictly greater than Resistant threshold (<).';
      } else if (formData.breakPointType === 'MIC' && sVal >= rVal) {
        errors.boundary = 'For MIC, Susceptible threshold (≤) must be strictly lower than Resistant threshold (>).';
      }
    }

    if (iMin !== null && iMax !== null && iMin > iMax) {
      errors.intermediate = 'Intermediate minimum cannot be greater than intermediate maximum.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save Breakpoint Rule
  const handleSaveBreakpoint = async (targetStatus) => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        breakpointID: formData.breakpointID,
        versionID: parseInt(formData.versionID, 10),
        organismID: targetLevel === 'SPECIFIC' && formData.organismID ? parseInt(formData.organismID, 10) : null,
        organismGroupID: targetLevel === 'GROUP' && formData.organismGroupID ? parseInt(formData.organismGroupID, 10) : null,
        antimicrobialID: parseInt(formData.antimicrobialID, 10),
        astMethodID: parseInt(formData.astMethodID, 10),
        siteOfInfection: formData.siteOfInfection || 'Systemic',
        breakPointType: formData.breakPointType,
        susceptibleThreshold: formData.susceptibleThreshold !== '' ? parseFloat(formData.susceptibleThreshold) : null,
        intermediateMin: formData.intermediateMin !== '' ? parseFloat(formData.intermediateMin) : null,
        intermediateMax: formData.intermediateMax !== '' ? parseFloat(formData.intermediateMax) : null,
        resistantThreshold: formData.resistantThreshold !== '' ? parseFloat(formData.resistantThreshold) : null,
        ruleExplanation: ruleTracePreview,
        isActive: targetStatus === 'Active'
      };

      await microbiologyService.saveBreakpoint(payload);
      setIsModalOpen(false);
      loadData();
    } catch (err) {
      setValidationErrors((prev) => ({
        ...prev,
        submit: err.response?.data?.message || 'Error saving breakpoint rule. Please check server.'
      }));
    }
  };

  // Filtered & Paginated Breakpoints
  const filteredBreakpoints = useMemo(() => {
    return breakpoints.filter((bp) => {
      if (selectedStatus === 'ACTIVE' && !bp.isActive) return false;
      if (selectedStatus === 'INACTIVE' && bp.isActive) return false;
      if (selectedType && bp.breakPointType !== selectedType) return false;
      if (selectedMethod && bp.astMethodID !== parseInt(selectedMethod, 10)) return false;

      if (search.trim()) {
        const q = search.toLowerCase();
        const orgMatch = (bp.organismName || bp.groupName || '').toLowerCase().includes(q);
        const drugMatch = (bp.drugName || bp.drugCode || '').toLowerCase().includes(q);
        const stdMatch = (bp.standardCode || bp.versionCode || '').toLowerCase().includes(q);
        const methodMatch = (bp.methodName || '').toLowerCase().includes(q);
        const ruleMatch = (bp.ruleExplanation || '').toLowerCase().includes(q);
        if (!orgMatch && !drugMatch && !stdMatch && !methodMatch && !ruleMatch) {
          return false;
        }
      }
      return true;
    });
  }, [breakpoints, selectedStatus, selectedType, selectedMethod, search]);

  // Centralized Standardized Pagination for Breakpoints
  const pagination = usePagination({
    items: filteredBreakpoints,
    initialPage: 1,
    initialPageSize: 15,
    resetDeps: [selectedStatus, selectedType, selectedMethod, search, selectedVersion, selectedOrganism, selectedDrug]
  });

  return (
    <div className="micro-master-container">
      {/* Header Section with Engine Status Indicator */}
      <div className="micro-header-section">
        <div className="micro-header-title">
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            Breakpoint Engine &amp; Clinical Standard Matrix
            <span className="micro-status-indicator">
              <span className="micro-status-dot" />
              Engine Status: Operational
            </span>
          </h1>
          <p>Manage versioned antimicrobial susceptibility interpretation thresholds and laboratory breakpoint rules</p>
        </div>
        <div className="micro-header-actions">
          <button className="micro-btn-primary" onClick={handleOpenAdd}>
            + Configure Breakpoint
          </button>
        </div>
      </div>

      {/* Breakpoint Calculation / Validation Sandbox */}
      <div className="micro-calc-panel">
        <div className="micro-calc-panel-header">
          <div>
            <h3>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <line x1="8" y1="6" x2="16" y2="6" />
                <line x1="16" y1="14" x2="16" y2="18" />
                <path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M8 18h.01M12 18h.01" />
              </svg>
              Breakpoint Calculation &amp; Sandbox Evaluation
            </h3>
            <p>Evaluate a measured AST result against the selected validated breakpoint rule</p>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
            Active Standards: <strong>EUCAST v16.1 &amp; CLSI M100 Ed.36</strong>
          </div>
        </div>

        <div className="micro-calc-grid">
          <div className="micro-form-group">
            <label>Test Organism <span className="req-star">*</span></label>
            <select
              className="micro-filter-select"
              value={calcOrgId}
              onChange={(e) => setCalcOrgId(e.target.value)}
            >
              {organisms.map((o) => (
                <option key={o.organismID} value={o.organismID}>
                  {o.organismName} ({o.groupName})
                </option>
              ))}
            </select>
          </div>

          <div className="micro-form-group">
            <label>Antimicrobial Drug <span className="req-star">*</span></label>
            <select
              className="micro-filter-select"
              value={calcDrugId}
              onChange={(e) => setCalcDrugId(e.target.value)}
            >
              {antimicrobials.map((d) => (
                <option key={d.antimicrobialID} value={d.antimicrobialID}>
                  {d.drugCode} — {d.drugName}
                </option>
              ))}
            </select>
          </div>

          <div className="micro-form-group">
            <label>AST Method <span className="req-star">*</span></label>
            <select
              className="micro-filter-select"
              value={calcMethodId}
              onChange={(e) => handleCalcMethodChange(e.target.value)}
            >
              {astMethods.map((m) => (
                <option key={m.astMethodID} value={m.astMethodID}>
                  {m.methodName} ({m.measurementType || 'ZONE'})
                </option>
              ))}
            </select>
          </div>

          <div className="micro-form-group">
            <label>Measurement Type</label>
            <select
              className="micro-filter-select"
              value={calcType}
              onChange={(e) => setCalcType(e.target.value)}
            >
              <option value="ZONE">Zone Diameter (mm)</option>
              <option value="MIC">MIC (µg/mL)</option>
            </select>
          </div>

          <div className="micro-form-group" style={{ maxWidth: '140px' }}>
            <label>
              <span>{calcType === 'ZONE' ? 'Zone (mm)' : 'MIC (µg/mL)'} <span className="req-star">*</span></span>
            </label>
            <input
              type="number"
              step="0.1"
              placeholder={calcType === 'ZONE' ? 'e.g. 24' : 'e.g. 1.0'}
              value={calcValue}
              onChange={(e) => setCalcValue(e.target.value)}
            />
          </div>

          <button
            className="micro-btn-primary"
            style={{ padding: '9px 16px', height: '38px', whiteSpace: 'nowrap' }}
            disabled={calcLoading}
            onClick={handleRunCalculation}
          >
            {calcLoading ? 'Evaluating...' : 'Evaluate S/I/R'}
          </button>
        </div>

        {calcError && (
          <div style={{ marginTop: '10px', fontSize: '0.82rem', color: '#e11d48', fontWeight: 500 }}>
            {calcError}
          </div>
        )}

        {/* Calculation Result Display */}
        {calcResult && (
          <div className="micro-calc-result-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.86rem', fontWeight: 700, color: '#334155' }}>
                Interpretation Result:
              </span>
              <span
                className="micro-badge"
                style={{
                  fontSize: '0.92rem',
                  padding: '4px 14px',
                  fontWeight: 700,
                  background:
                    calcResult.interpretation === 'S'
                      ? '#dcfce7'
                      : calcResult.interpretation === 'R'
                      ? '#fee2e2'
                      : calcResult.interpretation === 'I'
                      ? '#fef9c3'
                      : '#f1f5f9',
                  color:
                    calcResult.interpretation === 'S'
                      ? '#15803d'
                      : calcResult.interpretation === 'R'
                      ? '#b91c1c'
                      : calcResult.interpretation === 'I'
                      ? '#854d0e'
                      : '#475569'
                }}
              >
                {calcResult.interpretation === 'S'
                  ? 'S (Susceptible / Sensitive)'
                  : calcResult.interpretation === 'R'
                  ? 'R (Resistant)'
                  : calcResult.interpretation === 'I'
                  ? 'I (Intermediate / Increased Exposure)'
                  : 'NB (No Breakpoint Mapped)'}
              </span>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Standard: <strong>{calcResult.appliedStandard} {calcResult.appliedVersion}</strong>
              </span>
            </div>

            <div style={{ fontSize: '0.8rem', color: '#475569', fontStyle: 'italic', maxWidth: '520px' }}>
              <strong>Applied Rule:</strong> {calcResult.ruleTrace}
            </div>
          </div>
        )}
      </div>

      {/* Filter & Search Bar */}
      <div className="micro-filter-bar">

        <div className="micro-search-input">
          <input
            type="text"
            placeholder="Search by organism, drug, standard or rule trace..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="micro-filter-select"
          value={selectedVersion}
          onChange={(e) => setSelectedVersion(e.target.value)}
        >
          <option value="">All Standards &amp; Versions</option>
          <option value="1">EUCAST v16.1 (2026)</option>
          <option value="2">CLSI M100 36th Edition (2026)</option>
        </select>

        <select
          className="micro-filter-select"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <option value="">All Measurement Types</option>
          <option value="ZONE">Zone Diameter (ZONE)</option>
          <option value="MIC">MIC (µg/mL)</option>
        </select>

        <select
          className="micro-filter-select"
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
        >
          <option value="">All AST Methods</option>
          {astMethods.map((m) => (
            <option key={m.astMethodID} value={m.astMethodID}>
              {m.methodName}
            </option>
          ))}
        </select>

        <select
          className="micro-filter-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="ALL">All Statuses</option>
          <option value="ACTIVE">Active Rules Only</option>
          <option value="INACTIVE">Draft / Inactive</option>
        </select>
      </div>


      {/* Breakpoint Matrix Table */}
      <div className="micro-table-wrapper">
        <table className="micro-table">
          <thead>
            <tr>
              <th>Standard &amp; Version</th>
              <th>Target Level / Organism</th>
              <th>Antimicrobial</th>
              <th>AST Method</th>
              <th>Measurement</th>
              <th>Susceptible (S)</th>
              <th>Intermediate (I)</th>
              <th>Resistant (R)</th>
              <th>Status</th>
              <th>Rule Explanation</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '32px' }}>
                  Loading clinical breakpoint matrix...
                </td>
              </tr>
            ) : filteredBreakpoints.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '32px' }}>
                  No breakpoint thresholds match your filter criteria.
                </td>
              </tr>
            ) : (
              pagination.pagedItems.map((bp) => {
                const isZone = bp.breakPointType === 'ZONE';
                const sOp = isZone ? '≥' : '≤';
                const rOp = isZone ? '<' : '>';
                const unit = isZone ? 'mm' : 'µg/mL';

                return (
                  <tr key={bp.breakpointID}>
                    <td>
                      <strong>{bp.standardCode || 'CLSI'} {bp.versionCode || 'v2026'}</strong>
                    </td>
                    <td>
                      <div>{bp.organismName || bp.groupName || 'All Organisms'}</div>
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {bp.organismName ? 'Specific Organism' : 'Taxonomic Group'}
                      </span>
                    </td>
                    <td>
                      <strong>{bp.drugCode}</strong> — {bp.drugName}
                    </td>
                    <td>{bp.methodName}</td>
                    <td>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                        {bp.breakPointType} ({unit})
                      </span>
                    </td>
                    <td>
                      {bp.susceptibleThreshold !== null ? (
                        <span className="micro-badge badge-s">
                          {sOp} {bp.susceptibleThreshold} {unit}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {bp.intermediateMin !== null && bp.intermediateMax !== null ? (
                        <span className="micro-badge badge-i">
                          {bp.intermediateMin}–{bp.intermediateMax} {unit}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {bp.resistantThreshold !== null ? (
                        <span className="micro-badge badge-r">
                          {rOp} {bp.resistantThreshold} {unit}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span
                        className="micro-badge"
                        style={{
                          background: bp.isActive ? '#dcfce7' : '#f1f5f9',
                          color: bp.isActive ? '#15803d' : '#64748b'
                        }}
                      >
                        {bp.isActive ? 'Active' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.76rem', color: '#475569', maxWidth: '240px' }} title={bp.ruleExplanation}>
                      {bp.ruleExplanation ? (
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {bp.ruleExplanation}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Standard clinical threshold</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          className="micro-btn-secondary"
                          style={{ padding: '3px 8px', fontSize: '0.76rem' }}
                          onClick={() => handleOpenEdit(bp)}
                        >
                          Edit
                        </button>
                        <button
                          className="micro-btn-secondary"
                          style={{ padding: '3px 8px', fontSize: '0.76rem', color: '#0369a1', borderColor: '#bae6fd' }}
                          onClick={() => handleDuplicate(bp)}
                          title="Duplicate as New Version"
                        >
                          Clone
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Centralized Standard LIMS Pagination */}
      <CommonPagination
        {...pagination.paginationProps}
        loading={loading}
      />

      {/* Redesigned Configure Breakpoint Rule Modal */}
      {isModalOpen && (
        <div className="micro-modal-overlay" role="dialog" aria-modal="true">
          <div className="micro-modal-content">
            {/* Header */}
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  {editingBreakpoint ? 'Edit Breakpoint Rule' : 'Configure Breakpoint Rule'}
                </h3>
                <p>Define versioned interpretation thresholds, mathematical operators, and target organism mapping</p>
              </div>
              <button
                className="micro-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                title="Close"
                aria-label="Close form"
              >
                &times;
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="micro-modal-body">
              {validationErrors.submit && (
                <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.84rem' }}>
                  {validationErrors.submit}
                </div>
              )}

              {validationErrors.boundary && (
                <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.84rem' }}>
                  <strong>Boundary Conflict:</strong> {validationErrors.boundary}
                </div>
              )}

              {/* SECTION 1: Standard Context & Lifecycle */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>1. Standard Context &amp; Lifecycle</span>
                  <span className="section-badge">Guideline Source</span>
                </div>
                <div className="micro-form-grid-4">
                  <div className="micro-form-group">
                    <label>Standard Version <span className="req-star">*</span></label>
                    <select
                      value={formData.versionID}
                      onChange={(e) => setFormData({ ...formData, versionID: parseInt(e.target.value, 10) })}
                    >
                      <option value={1}>EUCAST v16.1 (2026)</option>
                      <option value={2}>CLSI M100 36th Edition (2026)</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Effective From <span className="req-star">*</span></label>
                    <input
                      type="date"
                      value={formData.effectiveFrom}
                      onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Effective To</label>
                    <input
                      type="date"
                      value={formData.effectiveTo}
                      onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Status <span className="req-star">*</span></label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Under Review">Under Review</option>
                      <option value="Approved">Approved</option>
                      <option value="Active">Active</option>
                      <option value="Retired">Retired</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION 2: Target Organism & Antimicrobial Drug Scope */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>2. Target Organism &amp; Drug Scope</span>
                  <span className="section-badge">Microbiology Scope</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>Target Level <span className="req-star">*</span></label>
                    <select
                      value={targetLevel}
                      onChange={(e) => {
                        const lvl = e.target.value;
                        setTargetLevel(lvl);
                        if (lvl === 'GROUP') {
                          setFormData((prev) => ({ ...prev, organismID: '', organismGroupID: groups[0]?.organismGroupID || 1 }));
                        } else {
                          setFormData((prev) => ({ ...prev, organismGroupID: '', organismID: organisms[0]?.organismID || 1 }));
                        }
                      }}
                    >
                      <option value="GROUP">Taxonomic Group / Species Complex</option>
                      <option value="SPECIFIC">Specific Microorganism</option>
                    </select>
                  </div>

                  {targetLevel === 'GROUP' ? (
                    <div className="micro-form-group">
                      <label>Taxonomic Group <span className="req-star">*</span></label>
                      <select
                        className={validationErrors.organismGroupID ? 'is-invalid' : ''}
                        value={formData.organismGroupID}
                        onChange={(e) => setFormData({ ...formData, organismGroupID: e.target.value })}
                      >
                        {groups.map((g) => (
                          <option key={g.organismGroupID} value={g.organismGroupID}>
                            {g.groupName}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="micro-form-group">
                      <label>Specific Organism <span className="req-star">*</span></label>
                      <select
                        className={validationErrors.organismID ? 'is-invalid' : ''}
                        value={formData.organismID}
                        onChange={(e) => setFormData({ ...formData, organismID: e.target.value })}
                      >
                        {organisms.map((o) => (
                          <option key={o.organismID} value={o.organismID}>
                            {o.organismName} ({o.organismCode})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="micro-form-group">
                    <label>Antimicrobial Drug <span className="req-star">*</span></label>
                    <select
                      className={validationErrors.antimicrobialID ? 'is-invalid' : ''}
                      value={formData.antimicrobialID}
                      onChange={(e) => setFormData({ ...formData, antimicrobialID: e.target.value })}
                    >
                      {antimicrobials.map((d) => (
                        <option key={d.antimicrobialID} value={d.antimicrobialID}>
                          {d.drugCode} — {d.drugName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>AST Method <span className="req-star">*</span></label>
                    <select
                      value={formData.astMethodID}
                      onChange={(e) => {
                        const mId = parseInt(e.target.value, 10);
                        const method = astMethods.find((m) => m.astMethodID === mId);
                        const newType = method?.measurementType || formData.breakPointType;
                        setFormData({
                          ...formData,
                          astMethodID: mId,
                          breakPointType: newType,
                          sOperator: newType === 'ZONE' ? '>=' : '<=',
                          rOperator: newType === 'ZONE' ? '<' : '>'
                        });
                      }}
                    >
                      {astMethods.map((m) => (
                        <option key={m.astMethodID} value={m.astMethodID}>
                          {m.methodName} ({m.measuredUnit})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Site of Infection</label>
                    <select
                      value={formData.siteOfInfection}
                      onChange={(e) => setFormData({ ...formData, siteOfInfection: e.target.value })}
                    >
                      <option value="Systemic">Systemic / Non-Meningitis</option>
                      <option value="Urinary Tract Specific">Urinary Tract Specific</option>
                      <option value="Meningitis / CSF">Meningitis / CSF</option>
                      <option value="Respiratory">Respiratory / Pulmonary</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Measurement Type <span className="req-star">*</span></label>
                    <select
                      value={formData.breakPointType}
                      onChange={(e) => {
                        const t = e.target.value;
                        setFormData({
                          ...formData,
                          breakPointType: t,
                          sOperator: t === 'ZONE' ? '>=' : '<=',
                          rOperator: t === 'ZONE' ? '<' : '>'
                        });
                      }}
                    >
                      <option value="ZONE">Zone Diameter (mm)</option>
                      <option value="MIC">MIC (µg/mL)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION 3: Breakpoint Thresholds & Direction */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>3. Breakpoint Thresholds</span>
                  <span className="section-badge">Mathematical Cutoffs ({formData.breakPointType === 'ZONE' ? 'mm' : 'µg/mL'})</span>
                </div>
                <div className="micro-form-grid-3">
                  {/* Susceptible Threshold */}
                  <div className="micro-form-group">
                    <label>
                      <span>Susceptible (S) Threshold <span className="req-star">*</span></span>
                    </label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#15803d', width: '28px', textAlign: 'center' }}>
                        {formData.breakPointType === 'ZONE' ? '≥' : '≤'}
                      </span>
                      <input
                        type="number"
                        step="0.1"
                        className={validationErrors.susceptibleThreshold ? 'is-invalid' : ''}
                        placeholder={formData.breakPointType === 'ZONE' ? 'e.g. 25' : 'e.g. 1.0'}
                        value={formData.susceptibleThreshold}
                        onChange={(e) => setFormData({ ...formData, susceptibleThreshold: e.target.value })}
                      />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {formData.breakPointType === 'ZONE' ? 'mm' : 'µg/mL'}
                      </span>
                    </div>
                    {validationErrors.susceptibleThreshold && (
                      <span className="micro-form-error">{validationErrors.susceptibleThreshold}</span>
                    )}
                  </div>

                  {/* Intermediate Range */}
                  <div className="micro-form-group">
                    <label>Intermediate (I) Range (Optional)</label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Min (e.g. 22)"
                        value={formData.intermediateMin}
                        onChange={(e) => setFormData({ ...formData, intermediateMin: e.target.value })}
                      />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>to</span>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Max (e.g. 24)"
                        value={formData.intermediateMax}
                        onChange={(e) => setFormData({ ...formData, intermediateMax: e.target.value })}
                      />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {formData.breakPointType === 'ZONE' ? 'mm' : 'µg/mL'}
                      </span>
                    </div>
                    {validationErrors.intermediate && (
                      <span className="micro-form-error">{validationErrors.intermediate}</span>
                    )}
                  </div>

                  {/* Resistant Threshold */}
                  <div className="micro-form-group">
                    <label>
                      <span>Resistant (R) Threshold <span className="req-star">*</span></span>
                    </label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#b91c1c', width: '28px', textAlign: 'center' }}>
                        {formData.breakPointType === 'ZONE' ? '<' : '>'}
                      </span>
                      <input
                        type="number"
                        step="0.1"
                        className={validationErrors.resistantThreshold ? 'is-invalid' : ''}
                        placeholder={formData.breakPointType === 'ZONE' ? 'e.g. 22' : 'e.g. 4.0'}
                        value={formData.resistantThreshold}
                        onChange={(e) => setFormData({ ...formData, resistantThreshold: e.target.value })}
                      />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {formData.breakPointType === 'ZONE' ? 'mm' : 'µg/mL'}
                      </span>
                    </div>
                    {validationErrors.resistantThreshold && (
                      <span className="micro-form-error">{validationErrors.resistantThreshold}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION 4: Real-time Rule Explanation & Preview */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>4. Rule Trace &amp; Preview</span>
                  <span className="section-badge">Auto-Generated Trace</span>
                </div>

                <div className="micro-rule-preview-box">
                  <div className="rule-title">Auto-Generated Interpretation Trace</div>
                  <div className="rule-content">{ruleTracePreview}</div>
                </div>

                <div className="micro-form-group" style={{ marginTop: '12px' }}>
                  <label>Reference / Implementation Notes (Optional)</label>
                  <textarea
                    rows="2"
                    placeholder="e.g. Footnote 1: Strains with MIC above breakpoint are extremely rare or not yet reported..."
                    value={formData.referenceNotes}
                    onChange={(e) => setFormData({ ...formData, referenceNotes: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer with Actions */}
            <div className="micro-modal-footer">
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                <span style={{ color: '#e11d48', fontWeight: 600 }}>*</span> Required breakpoint parameters
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  style={{ background: '#f8fafc', borderColor: '#cbd5e1', color: '#334155' }}
                  onClick={() => handleSaveBreakpoint('Draft')}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="micro-btn-success"
                  onClick={() => handleSaveBreakpoint('Active')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save &amp; Activate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
